import React, {Component} from 'react'
import propTypes from 'prop-types'
import {get} from 'lodash'
import iconNames from 'theme/icons'
import Prompt from 'components/prompt'
import {StringEditor} from 'components/inlineEditor'
import NodeTree from './components/nodeTree'
import Loading from 'components/loading'
import {SortableContainer} from 'react-sortable-hoc'
import './treeView.scss'

class TreeView extends Component {
  constructor(props) {
    super(props)
    this.state = {
      addingFolder: false,
      addTarget: null,
      renameNode: null,
      renameTarget: null,
      deletePrompt: false,
      deleteKey: null,
    }
  }

  selectNode = (key) => {
    const {onSelect} = this.props
    if (onSelect)
      onSelect(key)
  }

  toggleExpanded = (key, expanded) => {
    const {onExpand, onCollapse} = this.props
    if (expanded) {
      onExpand(key)
    } else {
      onCollapse(key)
    }
  }

  add = (e, parentKey, target) => {
    const {addPlaceholder} = this.props
    this.setState({
      renameNode: {label: addPlaceholder, parentKey},
      renameTarget: target,
    })
  }

  rename = (e, key, target) => {
    const {nodes} = this.props
    this.setState({
      renameNode: nodes.find(node => node.key === key),
      renameTarget: target,
    })
  }

  cancelRename = () => {
    this.setState({renameNode: null})
  }

  doRename = (value) => {
    const {renameNode} = this.state
    if (renameNode.key) {
      this.props.onRename(renameNode.key, value)
    } else {
      this.props.onAdd(renameNode.parentKey, value)
      this.ensureVisible(renameNode.parentKey)
    }
    this.setState({renameNode: null})
  }

  deleteCheck = (e, key) => {
    const {onDeleteCheck} = this.props
    if (onDeleteCheck) {
      onDeleteCheck(key)
    } else {
      this.setState({deletePrompt: true, deleteKey: key})
    }
  }

  cancelDelete = () => {
    this.setState({deletePrompt: false, deleteKey: null})
  }

  doDelete = () => {
    this.props.onDelete(this.state.deleteKey)
    this.setState({deletePrompt: false, deleteKey: null})
  }

  ensureVisible = (key) => {
    const {nodes, expandedNodes, onExpand} = this.props
    const node = nodes.find(node => node.key === key)
    if (node && node.parentKey) this.ensureVisible(node.parentKey)
    if (expandedNodes.indexOf(key) < 0) onExpand(key)
  }

  calcRecursiveCount = (node, nodes) => {
    let nodeTotalCount = node.count || 0
    const children = nodes.filter(node => (get(node, 'parentKey', 0) || 0) === node.key)
    children.forEach(node => {
      node.count = this.calcRecursiveCount(node, nodes)
      nodeTotalCount += node.count
    })
    return nodeTotalCount
  }

  getRootNode = (nodes, rootLabel, rootCount, showRecursiveCounts) => {
    const rootNode = {
      key: 0,
      label: rootLabel,
      count: rootCount,
    }

    if (showRecursiveCounts)
      rootNode.count = this.calcRecursiveCount(rootNode, nodes)

    return rootNode
  }

  getDeleteLabel = (nodes, deleteKey, deletePrompt) => {
    const deleteNode = nodes.find(n => n.key === deleteKey)
    return deleteNode && deletePrompt
        ? `Are you sure you wish to delete '${deleteNode.label}'?`
        : null
  }

  isMoveAllowed = (allowMove, sortable) => {
    return sortable
        ? false
        : allowMove
  }

  render() {
    const {deletePrompt, deleteKey, renameNode, renameTarget} = this.state
    const {
      nodes, rootLabel, rootCount, selectedNode, loading, expandedNodes = [], showCounts, selectedNodeRef,
      showRecursiveCounts, customActions, addLabel, addFolderIcon, folderIcon, selectedFolderIcon,
      collapsedIcon, expandedIcon, allowAdd, allowRename, allowDelete, allowExternalDrop, actions,
      onMove, onCanExternalDrop, onExternalDrop, maxDepth, sortable, nodeLabelMaxLength, onSortStart,
      onSortMove, onSortEnd, isActionEnabled, isPlaceholderVisible, placeholderData, allowMove, orderNodes,
    } = this.props
    const deleteLabel = this.getDeleteLabel(nodes, deleteKey, deletePrompt)
    const rootNode = this.getRootNode(nodes, rootLabel, rootCount, showRecursiveCounts)

    return (
        <div className='treeView'>
          {loading
              ? <Loading/>
              : (
                  <React.Fragment>
                    <NodeTree
                        node={rootNode}
                        isActionEnabled={isActionEnabled}
                        selectedNode={selectedNode}
                        nodes={nodes}
                        orderNodes={orderNodes}
                        expandedNodes={expandedNodes}
                        showCounts={showCounts || showRecursiveCounts}
                        customActions={customActions}
                        onClick={this.selectNode}
                        onToggleExpand={this.toggleExpanded}
                        onAdd={allowAdd ? this.add : null}
                        onRename={allowRename ? this.rename : null}
                        onDelete={allowDelete ? this.deleteCheck : null}
                        onMove={this.isMoveAllowed(allowMove, sortable) ? onMove : null}
                        onDrop={allowExternalDrop ? onExternalDrop : null}
                        onCanDrop={allowExternalDrop ? onCanExternalDrop : null}
                        addLabel={addLabel}
                        addFolderIcon={addFolderIcon}
                        folderIcon={folderIcon}
                        selectedFolderIcon={selectedFolderIcon}
                        collapsedIcon={collapsedIcon}
                        expandedIcon={expandedIcon}
                        maxDepth={maxDepth}
                        depth={0}
                        onSortStart={onSortStart}
                        onSortMove={onSortMove}
                        onSortEnd={onSortEnd}
                        sortable={sortable}
                        useDragHandle={true}
                        lockAxis='y'
                        axis='y'
                        isPlaceholderVisible={isPlaceholderVisible}
                        placeholderData={placeholderData}
                        actions={actions}
                        selectedNodeRef={selectedNodeRef}
                    />
                    <StringEditor
                        open={renameNode !== null}
                        value={renameNode ? renameNode.label : ''}
                        anchor={renameTarget}
                        options={{length: nodeLabelMaxLength}}
                        commitLabel={renameNode && renameNode.key ? 'Update' : 'Add'}
                        onCommit={this.doRename}
                        onCancel={this.cancelRename}
                    />
                    <Prompt
                        title='Delete Folder'
                        message={deleteLabel}
                        open={deletePrompt}
                        className={'treeView-delete-prompt'}
                        actions={[
                            {title: 'No', onAction: this.cancelDelete},
                            {title: 'Yes', primary: true, onAction: this.doDelete},
                        ]}
                    />
                  </React.Fragment>
              )
          }
        </div>
    )
  }
}

TreeView.propTypes = {
  rootLabel: propTypes.string.isRequired,
  rootCount: propTypes.number,
  nodes: propTypes.arrayOf(propTypes.shape({
    key: propTypes.oneOfType([propTypes.string, propTypes.number]).isRequired,
    label: propTypes.string.isRequired,
    count: propTypes.number,
    parentKey: propTypes.oneOfType([propTypes.string, propTypes.number]),
    order: propTypes.number,
  })).isRequired,
  selectedNode: propTypes.oneOfType([propTypes.string, propTypes.number]),
  expandedNodes: propTypes.arrayOf(propTypes.oneOfType([propTypes.string, propTypes.number])).isRequired,
  loading: propTypes.bool,
  showCounts: propTypes.bool,
  showRecursiveCounts: propTypes.bool,
  maxDepth: propTypes.number,
  sortable: propTypes.bool,
  nodeLabelMaxLength: propTypes.number,
  onSortEnd: propTypes.func,
  onSortMove: propTypes.func,
  onSortStart: propTypes.func,
  customActions: propTypes.arrayOf(propTypes.shape({
    text: propTypes.string.isRequired,
    icon: propTypes.string.isRequired,
    onClick: propTypes.func.isRequired, // (e, key)
  })),
  allowAdd: propTypes.bool,
  allowRename: propTypes.bool,
  allowDelete: propTypes.bool,
  allowMove: propTypes.bool,
  allowExternalDrop: propTypes.bool,
  onSelect: propTypes.func,
  onExpand: propTypes.func.isRequired,
  onCollapse: propTypes.func.isRequired,
  // You should provide ONE of the following delete callbacks
  onDeleteCheck: propTypes.func,      // (key) if provided, called when the delete button is clicked
  onDelete: propTypes.func,           // (key) if provided, called after confirmation of deletion
  onRename: propTypes.func,           // (key, newValue)
  onAdd: propTypes.func,              // (parentKey, newValue)
  onMove: propTypes.func,             // (movingNode, droppingNode)
  onCanExternalDrop: propTypes.func,  // (node, dragData) => boolean
  onExternalDrop: propTypes.func,     // (node, dragData)
  addLabel: propTypes.string,
  addFolderIcon: propTypes.string,
  addPlaceholder: propTypes.string,
  folderIcon: propTypes.string,
  selectedFolderIcon: propTypes.string,
  collapsedIcon: propTypes.string,
  expandedIcon: propTypes.string,
  isActionEnabled: propTypes.func,
  isPlaceholderVisible: propTypes.bool,
  placeholderData: propTypes.shape({
    messageTitle: propTypes.string,
    messageIcon: propTypes.string,
    iconSize: propTypes.number,
    actions: propTypes.array,
  }),
  orderNodes: propTypes.bool,
  actions: propTypes.array,
  selectedNodeRef: propTypes.object,
}

TreeView.defaultProps = {
  addLabel: 'Add a Folder',
  addFolderIcon: iconNames.addFolder,
  addPlaceholder: 'New Folder',
  folderIcon: iconNames.folder,
  selectedFolderIcon: iconNames.folderActive,
  collapsedIcon: iconNames.folderCollapsed,
  expandedIcon: iconNames.folderExpanded,
  maxDepth: null,
  isActionEnabled: null,
  onSelect: null,
  isPlaceholderVisible: false,
  placeholderData: {
    messageTitle: 'Looks like there are no folders',
    messageIcon: iconNames.folder,
    iconSize: 36,
    actions: [],
  },
  orderNodes: false,
}

const SortableTreeView = SortableContainer(TreeView)

export {SortableTreeView}
export default TreeView
